/**
 * regionRiskConverters.js
 *
 * DashboardRegionRiskPage から抽出された純粋関数群
 * Region リスク画面固有のデータ変換・集計ロジックを提供
 */

/**
 * 値を Date オブジェクトに変換
 * @param {string|Date|null} value - 日付値
 * @returns {Date|null} Date オブジェクト、無効な場合は null
 */
export function toDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * 日付を「prefix MMM,DD,YYYY」形式にフォーマット
 * @param {Date|null} date - 日付オブジェクト
 * @param {string} prefix - プレフィックス文字列（例: "Start", "End"）
 * @returns {string} フォーマット済み文字列
 */
export function formatDateLabel(date, prefix) {
  if (!date) return `${prefix} -`;
  const month = date.toLocaleString('en-US', { month: 'short' });
  const day = String(date.getDate()).padStart(2, '0');
  return `${prefix} ${month},${day},${date.getFullYear()}`;
}

/**
 * パーセンテージを計算（0-100の範囲に収める）
 * @param {number} completedCount - 完了数
 * @param {number} totalCount - 総数
 * @returns {number} パーセンテージ（0-100）
 */
export function toPercent(completedCount, totalCount) {
  if (!totalCount) return 0;
  return Math.max(0, Math.min(100, Math.round((completedCount / totalCount) * 100)));
}

/**
 * リスクレベルを正規化（H/M/L に統一）
 * @param {string|null} text - リスクレベルテキスト（"High", "Middle", "Low" など）
 * @returns {string|null} 正規化後のレベル（"H"|"M"|"L"|null）
 */
export function normalizeImpactLevel(text) {
  const value = String(text || '').trim().toLowerCase();
  if (!value) return null;

  if (value === 'h' || value.includes('high') || value.includes('高')) return 'H';
  if (value === 'm' || value.includes('middle') || value.includes('medium') || value.includes('mid') || value.includes('中')) return 'M';
  if (value === 'l' || value.includes('low') || value.includes('低')) return 'L';

  return null;
}

/**
 * 地域ラベルを正規化（"OEM×対地" → "OEM対地"）
 * @param {string} rawValue - 生の地域ラベル
 * @returns {string} 正規化後のラベル
 */
export function normalizeRegionLabel(rawValue) {
  const value = String(rawValue || '').trim();
  if (!value) return '';
  const [oem, region] = value.split('×');
  if (oem && region) return `${oem}${region}`;
  return value.replace(/×/g, '');
}

/**
 * 担当者表示ラベルを生成
 * 複数名の場合は「先頭の名前 他◯名」形式に短縮する
 * @param {string|Array<string>|null} value - 担当者（配列・区切り文字列いずれも可）
 * @returns {string} 表示用ラベル（担当者なしの場合は空文字）
 */
export function formatOwnerLabel(value) {
  const names = (Array.isArray(value) ? value : String(value ?? '').split(/[,、;；\/／]/))
    .map((name) => String(name ?? '').trim())
    .filter(Boolean);

  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  return `${names[0]} 他${names.length - 1}名`;
}

/**
 * アイテム配列をリスクレベルでカウント
 * @param {Array<Object>} items - アイテム配列
 * @param {string} levelField - レベルフィールド名（'impact' or 'riskLevel'）
 * @returns {Object} {H: number, M: number, L: number}
 */
export function countByImpactLevel(items = [], levelField = 'impact') {
  return items.reduce(
    (acc, item) => {
      const level = normalizeImpactLevel(item[levelField]);
      if (level && acc[level] !== undefined) {
        acc[level] += 1;
      }
      return acc;
    },
    { H: 0, M: 0, L: 0 },
  );
}

/**
 * アイテム配列をレベルでグループ化
 * @param {Array<Object>} items - アイテム配列
 * @param {string} titleField - タイトルフィールド名
 * @param {string|null} type - アイテムタイプ（'problem', 'risk' など）
 * @param {string} levelField - レベルフィールド名（'impact' or 'riskLevel'）
 * @returns {Object} {H: Array, M: Array, L: Array}
 */
export function groupDetailsByImpact(items = [], titleField = 'title', type = null, levelField = 'impact') {
  return items.reduce(
    (acc, item) => {
      const level = normalizeImpactLevel(item[levelField]);
      if (level) {
        acc[level].push({
          title: item[titleField] || '(タイトルなし)',
          owner: formatOwnerLabel(item.owner),
          notionPageId: item.notion_page_id || '',
          rawRow: item,
          type,
        });
      }
      return acc;
    },
    { H: [], M: [], L: [] },
  );
}

/**
 * 行が完了済みかどうかを判定
 * @param {Object} row - レコード行
 * @returns {boolean} 完了している場合 true
 */
export function isRowCompleted(row = {}) {
  if (!row) return false;
  // 完了判定はステータス=完了のみ。担当領域完了日などの日付が入っていても完了扱いにしない。
  const status = row.status_name || row.status || row.statusName || '';
  return String(status) === '完了' || String(status).toLowerCase() === 'done';
}

/**
 * タスク詳細オブジェクトを生成
 * @param {Object} row - タスク行データ
 * @returns {Object} タスク詳細オブジェクト
 */
export function makeTaskDetail(row) {
  return {
    title: row.small_category || '(タイトルなし)',
    owner: formatOwnerLabel(row.actor_name),
    notionPageId: row.notion_page_id || '',
    rawRow: row,
    type: 'task',
  };
}

/**
 * タスクデータを集計（総数・予定・完了・遅延）
 * @param {Array<Object>} schedules - スケジュール配列
 * @returns {Object} {counts: {total, planned, completed, delayed}, details: {...}}
 */
export function calcTaskData(schedules) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const counts = { total: 0, planned: 0, completed: 0, delayed: 0 };
  const details = { total: [], planned: [], completed: [], delayed: [] };

  for (const row of schedules) {
    const smallItem = row.small_category || '';
    if (!smallItem) continue;
    if (!row.start_date_plan) continue;
    if (!row.end_date_plan) continue;

    const detail = makeTaskDetail(row);
    counts.total += 1;
    details.total.push(detail);

    const startDate = toDate(row.start_date_plan);
    const endDate = toDate(row.end_date_plan);
    const isStartDue = startDate && startDate < today;
    const isEndDue = endDate && endDate < today;
    const isDue = isEndDue || isStartDue;
    const status = row.status_name || '';
    const isCompleted = status === '完了';

    if (isDue) {
      counts.planned += 1;
      details.planned.push(detail);
    }

    if (isDue && isCompleted) {
      counts.completed += 1;
      details.completed.push(detail);
    } else if (isStartDue && !isEndDue && status && status !== '未着手') {
      counts.completed += 1;
      details.completed.push(detail);
    }

    if (isEndDue && !isCompleted) {
      counts.delayed += 1;
      details.delayed.push(detail);
    } else if (isStartDue && !isEndDue && (!status || status === '未着手')) {
      counts.delayed += 1;
      details.delayed.push(detail);
    }
  }

  return { counts, details };
}

/**
 * 地域別カードデータを構築
 * @param {Array<Object>} schedules - スケジュール配列
 * @param {Array<Object>} problems - 課題配列
 * @param {Array<Object>} risks - リスク配列
 * @returns {Array<Object>} 地域カードデータ配列
 */
export function buildRegionCards(schedules, problems, risks) {
  const grouped = new Map();

  schedules.forEach((item) => {
    const key = item.oem_region || '';
    if (!key) return;
    if (!grouped.has(key)) {
      grouped.set(key, { schedules: [], problems: [], risks: [] });
    }
    grouped.get(key).schedules.push(item);
  });

  problems.forEach((item) => {
    const key = item.oemRegion || '';
    if (!key) return;
    if (!grouped.has(key)) {
      grouped.set(key, { schedules: [], problems: [], risks: [] });
    }
    grouped.get(key).problems.push(item);
  });

  risks.forEach((item) => {
    const key = item.oemRegion || '';
    if (!key) return;
    if (!grouped.has(key)) {
      grouped.set(key, { schedules: [], problems: [], risks: [] });
    }
    grouped.get(key).risks.push(item);
  });

  return Array.from(grouped.entries())
    .map(([key, bucket]) => {
      const { counts: taskCounts, details: taskDetails } = calcTaskData(bucket.schedules);
      const progressRate = toPercent(taskCounts.completed, taskCounts.planned);

      const startDates = bucket.schedules
        .map((row) => toDate(row.start_date_plan))
        .filter(Boolean)
        .sort((a, b) => a.getTime() - b.getTime());

      const endDates = bucket.schedules
        .map((row) => toDate(row.end_date_plan))
        .filter(Boolean)
        .sort((a, b) => a.getTime() - b.getTime());

      const activeProblems = bucket.problems.filter((p) => {
        const st = p.status_name || p.status || p.statusName || '';
        return String(st) !== '完了' && String(st).toLowerCase() !== 'done' && !p.todo;
      });
      const activeRisks = bucket.risks.filter((r) => !isRowCompleted(r));

      const issueCounts = countByImpactLevel(activeProblems, 'impact');
      const riskCounts = countByImpactLevel(activeRisks, 'riskLevel');
      const issueDetails = groupDetailsByImpact(activeProblems, 'summary', 'problem', 'impact');
      const riskDetails = groupDetailsByImpact(activeRisks, 'title', 'risk', 'riskLevel');

      return {
        regionKey: key,
        regionName: normalizeRegionLabel(key),
        progressRate,
        achievementText: `本日までのタスク進捗率: ${progressRate}%`,
        startDateText: formatDateLabel(startDates[0], 'Start'),
        endDateText: formatDateLabel(endDates[endDates.length - 1], 'End'),
        taskCounts,
        taskDetails,
        issueCounts,
        riskCounts,
        issueDetails,
        riskDetails,
      };
    })
    .sort((a, b) => a.regionName.localeCompare(b.regionName, 'ja'));
}

/**
 * 影響度カウントから最大値を取得
 * @param {Object} counts - {H: number, M: number, L: number}
 * @returns {number} 最大カウント（最小値は1）
 */
export function maxImpactCount(counts) {
  return Math.max(1, counts.H || 0, counts.M || 0, counts.L || 0);
}
